import React, { useState, useRef, KeyboardEvent, ClipboardEvent } from 'react';

import { Recipient        ,
         recipientKey     ,
         recipientDisplay }  from '../../../dataTypes/adminManagment/types_addressBook';

import { parseRecipient        ,
         isValidRecipient      ,
         RECIPIENT_COLORS      ,
         RECIPIENT_ICONS       ,
         RECIPIENT_CHANNEL_LABELS }  from '../../../utils/utils_recipients';

import Icon from '../images/Icon';

// ─────────────────────────────────────────────────────────────────────────────
// Zone de saisie des destinataires d'un envoi — emails et WhatsApp mélangés.
//
// Deux différences avec EmailChipInput, qui reste le champ des envois purement
// email (invitation à une chaîne) :
//
//  1. **Une puce sait par où elle part.** Le canal se déduit de la saisie —
//     l'arobase tranche — et se voit : couleur et pictogramme distincts. Les
//     deux ensemble, jamais la couleur seule.
//
//  2. **Le composant est piloté par le parent.** EmailChipInput garde ses puces
//     en interne, ce qui obligeait à le remonter par sa `key` pour le vider.
//     Ici la liste vient du parent et y retourne : la vider suffit à l'effacer.
//
// Une puce venue du carnet d'adresses porte le nom de la personne ; saisie à la
// main, elle porte la coordonnée. Dans les deux cas l'infobulle donne la valeur
// réellement expédiée — c'est elle qui compte au moment de l'envoi.
// ─────────────────────────────────────────────────────────────────────────────

interface RecipientChipInputProps {
  value        : Recipient[];
  onChange     : (recipients: Recipient[]) => void;
  placeholder? : string;
}

const RecipientChipInput: React.FC<RecipientChipInputProps> = ({
  value                                                            ,
  onChange                                                         ,
  placeholder = 'alice@exemple.com, +33 6 12 34 56 78…'            ,
}) => {

  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  /** Ajout sans doublon : même canal ET même valeur est déjà dans la liste. */
  const append = (nouveaux: Recipient[]) => {

    const connus = new Set(value.map(recipientKey));
    const ajouts = nouveaux.filter(r => {
      const cle = recipientKey(r);
      if (connus.has(cle)) { return false; }
      connus.add(cle);
      return true;
    });

    if (ajouts.length === 0) { setInputValue(''); return; }

    onChange([...value, ...ajouts]);
    setInputValue('');
  };

  const commit = (raw: string) => {
    const trimmed = raw.trim().replace(/[,;]+$/, '');
    if (!trimmed) { return; }
    append([parseRecipient(trimmed)]);
  };

  const removeAt = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {

    // L'espace ne valide plus systématiquement, contrairement au champ email :
    // un numéro s'écrit « +33 6 12 34 56 78 », et la puce serait coupée au premier
    // espace. Il ne vaut validation que sur une saisie qui ne peut être qu'un
    // email — une adresse ne contient jamais d'espace.
    const valide = e.key === ' '
                   ? inputValue.includes('@')
                   : ['Enter', ',', ';', 'Tab'].includes(e.key);

    if (valide) {
      e.preventDefault();
      commit(inputValue);
    }
    if (e.key === 'Backspace' && inputValue === '' && value.length > 0) {
      removeAt(value.length - 1);
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    // Le point-virgule et la virgule séparent ; l'espace aussi, sauf qu'un numéro
    // écrit « +33 6 12 34 56 78 » en contient. On ne coupe donc sur l'espace que
    // si le texte collé porte un séparateur explicite.
    const colle    = e.clipboardData.getData('text');
    const morceaux = /[,;\n]/.test(colle) ? colle.split(/[,;\n]+/) : colle.split(/\s{2,}/);
    const propres  = morceaux.map(p => p.trim()).filter(Boolean);
    if (propres.length === 0) { return; }
    append(propres.map(parseRecipient));
  };

  return (
    <div
      onClick = {() => inputRef.current?.focus()}
      style   = {{
        display      : 'flex',
        flexWrap     : 'wrap',
        alignItems   : 'center',
        gap          : '6px',
        padding      : '6px 8px',
        background   : 'var(--color-TextArea-BG-normal)',
        border       : '1px solid var(--color-TextArea-Border-normal)',
        borderRadius : '6px',
        cursor       : 'text',
        minHeight    : '42px',
      }}
    >
      {value.map((recipient, index) => {

        const valide   = isValidRecipient(recipient);
        const couleurs = RECIPIENT_COLORS[recipient.channel];
        const canal    = RECIPIENT_CHANNEL_LABELS[recipient.channel];

        return (
          <span
            key   = {`${recipientKey(recipient)}-${index}`}
            title = {valide
                     ? `${canal} — ${recipient.value}`
                     : `${canal} invalide : ${recipient.value}`}
            style = {{
              display      : 'inline-flex',
              alignItems   : 'center',
              gap          : '5px',
              padding      : '2px 8px 2px 10px',
              borderRadius : '999px',
              fontSize     : '16px',
              fontWeight   : 500,
              lineHeight   : '20px',
              maxWidth     : '260px',
              overflow     : 'hidden',
              ...(valide
                ? couleurs
                : { background: 'var(--color-background-danger)', color: 'var(--color-text-danger)', border: '1px solid var(--color-border-danger)' }
              ),
            }}
          >
            <span aria-hidden="true" style={{ flexShrink: 0, display: 'flex' }}>
              <Icon name      = {RECIPIENT_ICONS[recipient.channel]}
                    width     = {14}
                    height    = {14}
                    color     = "currentColor"
                    hoverable = {false}
                    clickable = {false} />
            </span>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {recipientDisplay(recipient)}
            </span>
            <button
              onClick = {(e) => { e.stopPropagation(); removeAt(index); }}
              title   = "Supprimer"
              style   = {{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, lineHeight: 1, fontSize: '16px', display: 'flex', alignItems: 'center', opacity: 0.75, flexShrink: 0, color: 'inherit' }}
            >×</button>
          </span>
        );
      })}
      <input
        ref         = {inputRef}
        type        = "text"
        value       = {inputValue}
        onChange    = {e => setInputValue(e.target.value)}
        onKeyDown   = {handleKeyDown}
        onBlur      = {() => { if (inputValue.trim()) { commit(inputValue); } }}
        onPaste     = {handlePaste}
        placeholder = {value.length === 0 ? placeholder : ''}
        style       = {{
          flex       : '1 1 140px',
          minWidth   : '120px',
          border     : 'none',
          outline    : 'none',
          background : 'transparent',
          color      : 'var(--color-TextArea-FG-normal)',
          fontSize   : '16px',
          padding    : '2px 0',
        }}
      />
    </div>
  );
};

export default RecipientChipInput;
